import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { environment } from '../environments/environmentCAS';
import { BesoinClient, ResultatComparaison } from '../models/comparateur.model';

@Injectable({
  providedIn: 'root'
})
export class CompareService {
  // Force relative URLs in local frontend so Angular dev proxy is always used.
  private readonly apiUrl = '/flask-api/compare';
  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    })
  };

  // Loading state management
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Calls the backend to get comparison results based on client needs.
   * @param needs The payload containing slider values (BesoinClient).
   * @returns An Observable with the list of top 3 comparison results.
   */
  getComparisonResults(needs: BesoinClient): Observable<{ table: string }> {
    this.loadingSubject.next(true);

    return this.http.post<{ table: string }>(this.apiUrl, needs, this.httpOptions)
      .pipe(
        catchError(this.handleError.bind(this)),
        finalize(() => {
          this.loadingSubject.next(false);
        })
      );
  }

  /**
   * Calls the backend to extract enterprise formulas and premiums from a PDF.
   * @param file The PDF file.
   * @returns An Observable with the extracted enterprise data.
   */
  extractEnterpriseQuotes(file: File, passValue?: number): Observable<any> {
    this.loadingSubject.next(true);
    const formData = new FormData();
    formData.append('pdf_file', file);
    if (passValue) {
      formData.append('pass_value', passValue.toString());
    }

    const url = '/flask-api/extract-enterprise';
    
    return this.http.post<any>(url, formData)
      .pipe(
        catchError(this.handleError.bind(this)),
        finalize(() => {
          this.loadingSubject.next(false);
        })
      );
  }

  /**
   * Private method to handle HTTP errors.
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'Une erreur inattendue s\'est produite.';

    if (error.status === 0) {
      // Browser-level/network/proxy/CORS issue: backend not reachable from frontend.
      errorMessage = 'Impossible de se connecter au serveur. Vérifiez que Flask tourne sur http://127.0.0.1:5000 puis relancez ng serve.';
    } else if (error.error instanceof ErrorEvent) {
      errorMessage = `Erreur réseau: ${error.error.message}`;
    } else {
      const status = error.status ?? 'inconnu';
      const body = error.error;
      console.error(`Backend returned code ${status}, body was:`, body);

      if (typeof body === 'object' && body !== null && 'error' in body) {
        errorMessage = String((body as { error?: string }).error || error.message);
      } else if (typeof body === 'string' && (body.includes('<!DOCTYPE') || body.includes('<!doctype'))) {
        errorMessage =
          'Le serveur Flask a renvoyé une page d\'erreur HTML (souvent mode debug Windows). ' +
          'Relancez avec: python app.py (sans reloader), ou consultez la console Flask.';
      } else if (error.message?.includes('Unexpected token')) {
        errorMessage =
          'Réponse invalide du serveur Flask (JSON attendu). Vérifiez que python app.py est démarré et relancé.';
      } else {
        errorMessage = `Erreur du serveur (code ${status}): ${error.message}`;
      }
    }

    return throwError(() => new Error(errorMessage));
  }
}